import type { Camera, Clip, FileRef } from './footage';
import { timeOfSeq, type Telemetry } from './telemetry';
import { median, roundToQuarterHour, SECOND_MS } from './time';

const MIN_SLOT_GAP_MS = 40;
const MAX_SEQ_SHIFT_MS = 1000;
const MAX_SEQ_GAP_SHIFT_MS = 2000;
const SHARED_COUNTER_CAMERAS: ReadonlySet<Camera> = new Set(['left_repeater', 'right_repeater']);

export interface ClipHeader {
  readonly createdAt: number | undefined;
  readonly span: number;
  readonly firstSeq: number | undefined;
  readonly lastSeq: number | undefined;
}

export interface PlacedClip<F extends FileRef> {
  readonly clip: Clip<F>;
  readonly start: number;
  readonly span: number;
}

export interface TimelineSlot<F extends FileRef> {
  readonly slot: string;
  readonly start: number;
  readonly end: number;
  readonly clips: readonly PlacedClip<F>[];
}

export interface Timeline<F extends FileRef> {
  readonly start: number;
  readonly end: number;
  readonly utcOffset: number;
  readonly slots: readonly TimelineSlot<F>[];
}

export interface TimelineInput<F extends FileRef> {
  readonly clips: readonly Clip<F>[];
  readonly header: (clip: Clip<F>) => ClipHeader | undefined;
  readonly frontTelemetry: (slot: string) => Telemetry | undefined;
}

export interface ClipPosition<F extends FileRef> {
  readonly placed: PlacedClip<F>;
  readonly mediaTime: number;
}

interface Readable<F extends FileRef> {
  readonly clip: Clip<F>;
  readonly header: ClipHeader;
}

export function utcOffsetOf<F extends FileRef>(clips: readonly Clip<F>[], header: TimelineInput<F>['header']): number {
  const offsets = clips.flatMap((clip) => {
    const createdAt = header(clip)?.createdAt;
    return createdAt === undefined ? [] : [roundToQuarterHour(clip.slotTime - createdAt)];
  });
  return median(offsets) ?? 0;
}

function groupBySlot<F extends FileRef>(input: TimelineInput<F>): readonly (readonly Readable<F>[])[] {
  const slots = new Map<string, Readable<F>[]>();
  for (const clip of [...input.clips].sort((a, b) => a.slotTime - b.slotTime)) {
    const header = input.header(clip);
    if (header) slots.set(clip.slot, [...(slots.get(clip.slot) ?? []), { clip, header }]);
  }
  return [...slots.values()];
}

const frontOf = <T extends { readonly clip: Clip<FileRef> }>(entries: readonly T[]): T | undefined =>
  entries.find((entry) => entry.clip.camera === 'front');

function counterRate(telemetry: Telemetry | undefined): number | undefined {
  if (!telemetry || telemetry.seq.length < 2) return undefined;
  const rate = (telemetry.seq.at(-1)! - telemetry.seq[0]!) / (telemetry.time.at(-1)! - telemetry.time[0]!);
  return Number.isFinite(rate) && rate > 0 ? rate : undefined;
}

function chainedStart<F extends FileRef>(input: TimelineInput<F>, previous: TimelineSlot<F> | undefined, clips: readonly Readable<F>[], span: number): number | undefined {
  const before = previous && frontOf(previous.clips);
  const after = frontOf(clips);
  const lastSeq = before && input.header(before.clip)?.lastSeq;
  const firstSeq = after?.header.firstSeq;
  const rate = before && counterRate(input.frontTelemetry(before.clip.slot));
  if (!before || !after || lastSeq === undefined || firstSeq === undefined || rate === undefined || firstSeq <= lastSeq) return undefined;
  const frontStart = before.start + before.span * SECOND_MS + ((firstSeq - lastSeq) / rate) * SECOND_MS;
  return frontStart - (span - after.header.span) * SECOND_MS;
}

function slotStart<F extends FileRef>(input: TimelineInput<F>, previous: TimelineSlot<F> | undefined, clips: readonly Readable<F>[], utcOffset: number): number {
  const span = Math.max(...clips.map(({ header }) => header.span));
  const fromHeaders = Math.min(...clips.map(({ clip, header }) => header.createdAt ?? clip.slotTime - utcOffset));
  const chained = chainedStart(input, previous, clips, span);
  const start = chained !== undefined && Math.abs(chained - fromHeaders) <= MAX_SEQ_GAP_SHIFT_MS ? chained : fromHeaders;
  return previous ? Math.max(start, previous.end + MIN_SLOT_GAP_MS) : start;
}

function placeSlot<F extends FileRef>(input: TimelineInput<F>, previous: TimelineSlot<F> | undefined, clips: readonly Readable<F>[], utcOffset: number): TimelineSlot<F> {
  const start = slotStart(input, previous, clips, utcOffset);
  const end = start + Math.max(...clips.map(({ header }) => header.span)) * SECOND_MS;
  const placed = clips.map(({ clip, header }) => ({ clip, start: end - header.span * SECOND_MS, span: header.span }));
  return { slot: clips[0]!.clip.slot, start, end, clips: alignToFront(input, placed) };
}

function alignToFront<F extends FileRef>(input: TimelineInput<F>, placed: readonly PlacedClip<F>[]): readonly PlacedClip<F>[] {
  const front = frontOf(placed);
  const telemetry = front && input.frontTelemetry(front.clip.slot);
  if (!front || !telemetry) return placed;
  return placed.map((entry) => {
    const lastSeq = SHARED_COUNTER_CAMERAS.has(entry.clip.camera) ? input.header(entry.clip)?.lastSeq : undefined;
    const frontTime = lastSeq === undefined ? undefined : timeOfSeq(telemetry, lastSeq);
    if (frontTime === undefined) return entry;
    const start = front.start + (frontTime - entry.span) * SECOND_MS;
    return Math.abs(start - entry.start) <= MAX_SEQ_SHIFT_MS ? { ...entry, start } : entry;
  });
}

export function buildTimeline<F extends FileRef>(input: TimelineInput<F>): Timeline<F> | undefined {
  const utcOffset = utcOffsetOf(input.clips, input.header);
  const slots = groupBySlot(input).reduce<TimelineSlot<F>[]>((placed, clips) => [...placed, placeSlot(input, placed.at(-1), clips, utcOffset)], []);
  const first = slots[0];
  if (!first) return undefined;
  const start = Math.min(first.start, ...first.clips.map((entry) => entry.start));
  return { start, end: slots.at(-1)!.end, utcOffset, slots };
}

export const clampTrigger = <F extends FileRef>(timeline: Timeline<F>, localTimestamp: number | undefined): number | undefined =>
  localTimestamp === undefined ? undefined : Math.min(timeline.end, Math.max(timeline.start, localTimestamp - timeline.utcOffset));

export function positionAt<F extends FileRef>(timeline: Timeline<F>, camera: Camera, time: number): ClipPosition<F> | undefined {
  for (const slot of timeline.slots) {
    const placed = slot.clips.find((entry) => entry.clip.camera === camera);
    if (placed && time >= placed.start && time <= placed.start + placed.span * SECOND_MS) return { placed, mediaTime: (time - placed.start) / SECOND_MS };
  }
  return undefined;
}
